import { CompanyRepository, ReservationRepository, SettingsRepository } from '@backend/repository'
import { ReservationState } from '@core/types'
import { Injectable, Logger } from '@nestjs/common'
import { addHours, addMinutes } from 'date-fns'
import { In, IsNull, Not } from 'typeorm'
import { ReservationEmailService } from '../shared'
import { Cron } from '../../../base'

@Injectable()
export class ReservationReminderCron {
	private readonly logger = new Logger(ReservationReminderCron.name)

	constructor(
		private readonly _emailService: ReservationEmailService,
		private readonly _companyRepository: CompanyRepository,
		private readonly _settingsRepository: SettingsRepository,
		private readonly _reservationRepository: ReservationRepository,
	) {}

	@Cron({
		name: 'reservation_reminder',
		schedule: '0 */10 * * * *',
		appconfigScheduleKey: 'reservation_reminder_cron_schedule',
	})
	async sendReminders() {
		const filterStart = addHours(new Date(), 24)
		const filterEnd = addMinutes(filterStart, 10)

		const reservations = await this._reservationRepository.findByInterval(filterStart, filterEnd, {
			where: {
				state: ReservationState.approved,
				customerData: { email: Not(IsNull()) },
				reminderSent: false,
			},
		})

		if (reservations.length === 0) {
			this.logger.log('No reservations to send reminders for')
			return
		}

		const restaurantIds = reservations.map((reservation) => reservation.tenantId)
		const restaurants = await this._companyRepository.find({
			where: { id: In(restaurantIds) },
		})
		const restaurantMap = new Map(restaurants.map((restaurant) => [restaurant.id, restaurant]))

		const settings = await this._settingsRepository.find({
			where: { tenantId: In(restaurantIds) },
		})
		const settingsMap = new Map(settings.map((setting) => [setting.tenantId!, setting]))

		for (const reservation of reservations) {
			this.logger.log({ message: 'Sending reminder for reservation', reservationId: reservation.id })

			const restaurant = restaurantMap.get(reservation.tenantId as string)
			if (!restaurant) {
				this.logger.error({ message: 'Restaurant not found', tenantId: reservation.tenantId })
				continue
			}

			const settings = settingsMap.get(reservation.tenantId as string)
			if (!settings) {
				this.logger.error({ message: 'Settings not found', tenantId: reservation.tenantId })
				continue
			}

			await this._emailService.sendReminderEmail(reservation, settings, restaurant.name)

			await this._reservationRepository.update({ id: reservation.id }, { reminderSent: true })
		}
	}
}
